import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatCurrency, formatShortDate } from "@/lib/format";
import { AlertTriangle } from "lucide-react";
import SourceLogo from "./SourceLogo";

/**
 * Today's activity — right-hand operational panel.
 * Tabs: Sales / Cancellations / Overbookings.
 *
 * Data comes from a single `/reports/dashboard-activity` endpoint so
 * the three views are always consistent.
 */
export default function TodayActivityPanel({ activity, currency }) {
    const { t } = useTranslation();
    const [tab, setTab] = useState("sales");
    const sales    = activity?.sales    || { booked_today: 0, room_nights: 0, revenue: 0, reservations: [] };
    const cancels  = activity?.cancellations || { count: 0, room_nights_lost: 0, lost_value: 0, reservations: [] };
    const overbook = activity?.overbooking || { count: 0, situations: [] };

    const tabs = [
        { key: "sales",         label: t("dashboard.activity_sales"),         count: sales.booked_today },
        { key: "cancellations", label: t("dashboard.activity_cancellations"), count: cancels.count },
        { key: "overbookings",  label: t("dashboard.activity_overbookings"),  count: overbook.count },
    ];

    return (
        <section className="rounded-[10px] border border-border bg-card" data-testid="activity-panel">
            <div className="flex items-center justify-between px-4 pt-3.5">
                <h2 className="text-[15px] font-semibold text-foreground">{t("dashboard.activity_today")}</h2>
            </div>

            <div className="mt-2 flex items-center gap-5 border-b border-border px-4">
                {tabs.map((tb) => {
                    const isActive = tb.key === tab;
                    return (
                        <button
                            key={tb.key}
                            onClick={() => setTab(tb.key)}
                            data-testid={`activity-tab-${tb.key}`}
                            className={`relative -mb-px flex items-center gap-1.5 whitespace-nowrap pb-2.5 pt-1 text-[13px] transition ${
                                isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground/90"
                            }`}
                        >
                            <span className="font-medium">{tb.label}</span>
                            {tb.count > 0 && (
                                <span className={`rounded-full px-1.5 text-[10.5px] font-semibold tabular-nums ${
                                    isActive ? "bg-[#ff7b02]/10 text-[#ff7b02]" : "bg-muted text-muted-foreground"
                                }`}>
                                    {tb.count}
                                </span>
                            )}
                            {isActive && <span className="absolute inset-x-0 bottom-[-1px] h-[2px] rounded-t bg-[#ff7b02]" />}
                        </button>
                    );
                })}
            </div>

            {tab === "sales"         && <SalesView data={sales} currency={currency} />}
            {tab === "cancellations" && <CancellationsView data={cancels} currency={currency} />}
            {tab === "overbookings"  && <OverbookingsView data={overbook} />}
        </section>
    );
}

// ─────────────── Sales ───────────────
function SalesView({ data, currency }) {
    const { t } = useTranslation();
    return (
        <>
            <MetricRow items={[
                { label: t("dashboard.booked_today"),           value: data.booked_today, testid: "sales-booked" },
                { label: t("dashboard.unit_nights"),            value: data.room_nights, testid: "sales-nights" },
                { label: t("dashboard.revenue_from_bookings"),  value: formatCurrency(data.revenue, currency), testid: "sales-revenue" },
            ]} />

            {data.reservations.length === 0 ? (
                <div className="px-4 py-8 text-center text-[12.5px] text-muted-foreground/70">{t("dashboard.no_sales_today")}</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-[520px] w-full text-[12.5px]">
                        <thead>
                            <tr className="border-y border-border bg-muted/40 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                                <th className="px-4 py-2">{t("dashboard.col_guest")}</th>
                                <th className="px-4 py-2">{t("dashboard.col_revenue")}</th>
                                <th className="px-4 py-2">{t("dashboard.col_check_in")}</th>
                                <th className="px-4 py-2">{t("dashboard.col_nights")}</th>
                                <th className="px-4 py-2">{t("dashboard.col_source")}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border/60">
                            {data.reservations.slice(0, 8).map((r) => (
                                <tr key={r.id} className="hover:bg-muted/60">
                                    <td className="px-4 py-2.5">
                                        <Link to={`/reservations?id=${r.id}`} className="font-medium text-foreground hover:underline">
                                            {r.guest_first_name} {r.guest_last_name}
                                        </Link>
                                    </td>
                                    <td className="px-4 py-2.5 tabular-nums text-foreground">{formatCurrency(r.total_amount || 0, currency)}</td>
                                    <td className="px-4 py-2.5 tabular-nums text-muted-foreground">{formatShortDate(r.check_in)}</td>
                                    <td className="px-4 py-2.5 tabular-nums text-muted-foreground">
                                        {Math.max(0, Math.round((new Date(r.check_out) - new Date(r.check_in)) / 86400000))}
                                    </td>
                                    <td className="px-4 py-2.5"><SourceLogo source={r.source || "direct"} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}

// ─────────────── Cancellations ───────────────
function CancellationsView({ data, currency }) {
    const { t } = useTranslation();
    return (
        <>
            <MetricRow items={[
                { label: t("dashboard.cancels_today"),        value: data.count, testid: "cancel-count" },
                { label: t("dashboard.cancel_room_nights"),   value: data.room_nights_lost, testid: "cancel-nights" },
                { label: t("dashboard.cancel_lost_value"),    value: formatCurrency(data.lost_value, currency), testid: "cancel-value" },
            ]} />
            {data.reservations.length === 0 ? (
                <div className="px-4 py-8 text-center text-[12.5px] text-muted-foreground/70">{t("dashboard.no_cancels_today")}</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-[520px] w-full text-[12.5px]">
                        <thead>
                            <tr className="border-y border-border bg-muted/40 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                                <th className="px-4 py-2">{t("dashboard.col_guest")}</th>
                                <th className="px-4 py-2">{t("dashboard.col_reservation")}</th>
                                <th className="px-4 py-2">{t("dashboard.col_stay_date")}</th>
                                <th className="px-4 py-2">{t("dashboard.col_amount")}</th>
                                <th className="px-4 py-2">{t("dashboard.col_source")}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border/60">
                            {data.reservations.slice(0, 8).map((r) => (
                                <tr key={r.id} className="hover:bg-muted/60">
                                    <td className="px-4 py-2.5">
                                        <Link to={`/reservations?id=${r.id}`} className="font-medium text-foreground hover:underline">
                                            {r.guest_first_name} {r.guest_last_name}
                                        </Link>
                                    </td>
                                    <td className="px-4 py-2.5 font-mono text-[11.5px] text-muted-foreground">{r.confirmation_number || `AK-${(r.id || "").slice(0,6).toUpperCase()}`}</td>
                                    <td className="px-4 py-2.5 tabular-nums text-muted-foreground">{formatShortDate(r.check_in)} → {formatShortDate(r.check_out)}</td>
                                    <td className="px-4 py-2.5 tabular-nums text-foreground">{formatCurrency(r.total_amount || 0, currency)}</td>
                                    <td className="px-4 py-2.5"><SourceLogo source={r.source || "direct"} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}

// ─────────────── Overbookings ───────────────
function OverbookingsView({ data }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    // Aggregate summary — always shown, even when there are no situations,
    // so users see the panel state instead of an unexplained empty view.
    const dates = new Set(data.situations.map((s) => s.date));
    const types = new Set(data.situations.map((s) => s.room_type));
    return (
        <>
            <MetricRow items={[
                { label: t("dashboard.overbooking_active"),     value: data.count, testid: "over-count" },
                { label: t("dashboard.affected_dates"),         value: dates.size, testid: "over-dates" },
                { label: t("dashboard.affected_room_types"),    value: types.size, testid: "over-types" },
            ]} />
            {!data.situations.length ? (
                <div className="px-4 py-10 text-center text-[12.5px] text-muted-foreground/70">
                    {t("dashboard.no_overbooking")}
                </div>
            ) : (
                <div className="overflow-x-auto">
                <table className="min-w-[520px] w-full text-[12.5px]">
                    <thead>
                        <tr className="border-y border-border bg-muted/40 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                            <th className="px-4 py-2">{t("dashboard.col_stay_date")}</th>
                            <th className="px-4 py-2">{t("dashboard.col_room_type")}</th>
                            <th className="px-4 py-2 text-right">{t("dashboard.inventory")}</th>
                            <th className="px-4 py-2 text-right">{t("dashboard.reserved")}</th>
                            <th className="px-4 py-2 text-right">{t("dashboard.diff")}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border/60">
                        {data.situations.slice(0, 12).map((s, i) => (
                            <tr
                                key={`${s.date}-${s.room_type_id || i}`}
                                onClick={() => navigate(`/reservations?date=${s.date}&room_type=${s.room_type_id || ""}`)}
                                className="cursor-pointer hover:bg-rose-50/40"
                            >
                                <td className="px-4 py-2.5 tabular-nums text-foreground">{formatShortDate(s.date)}</td>
                                <td className="px-4 py-2.5 text-foreground/90">
                                    <span className="inline-flex items-center gap-1.5">
                                        <AlertTriangle size={12} className="text-rose-500" />
                                        {s.room_type}
                                    </span>
                                </td>
                                <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">{s.inventory}</td>
                                <td className="px-4 py-2.5 text-right tabular-nums text-foreground">{s.reserved}</td>
                                <td className="px-4 py-2.5 text-right tabular-nums font-medium text-rose-700">{s.diff}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            )}
        </>
    );
}

// ─────────────── Small compact "3 metric" strip inside a tab ───────────────
function MetricRow({ items }) {
    return (
        <div className="grid grid-cols-3 divide-x divide-border border-b border-border">
            {items.map((it, i) => (
                <div key={i} className="px-4 py-3" data-testid={it.testid}>
                    <div className="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground">{it.label}</div>
                    <div className="mt-1 text-[18px] font-semibold leading-none text-foreground tabular-nums">
                        {it.value}
                    </div>
                </div>
            ))}
        </div>
    );
}
